// Central API helper - baseURL points to our Express backend
const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.')
);

const envBaseUrl = process.env.REACT_APP_BASE_URL;
const isEnvLocalhost = !envBaseUrl || envBaseUrl.includes('localhost') || envBaseUrl.includes('127.0.0.1');

export const BASE_URL = isLocalhost 
    ? 'http://localhost:5000' 
    : (!isEnvLocalhost ? envBaseUrl : (typeof window !== 'undefined' ? window.location.origin : ''));
export const API_BASE = `${BASE_URL}/api`;
export const UPLOAD_PATH = process.env.REACT_APP_UPLOAD_PATH || 'uploads';

export const getAuthToken = (token?: string): string => {
    if (token && typeof token === 'string' && token.trim() !== '') return token;
    if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem('resido_token') || localStorage.getItem('token') || '';
    }
    return '';
};

const safeJsonResponse = async (res: Response) => {
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        return res.json();
    }
    const text = await res.text();
    return { success: false, message: text || `HTTP ${res.status}: Unexpected response` };
};

/**
 * Resolves the full URL for an image or document path.
 * Handles both absolute URLs and relative paths stored in the DB.
 */
export const getImageUrl = (path?: string) => {
    if (!path || path === '' || path.includes('p-1.jpg')) return '/logo.png';
    if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) return path;

    // If it's a relative path to the client's public folder
    if (path.startsWith('/') && !path.startsWith('/uploads/')) {
        return path;
    }

    // Ensure path doesn't have a leading slash
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    // Check if it already starts with the configured upload path
    if (cleanPath.startsWith(`${UPLOAD_PATH}/`)) {
        return `${BASE_URL}/${cleanPath}`;
    }

    return `${BASE_URL}/${UPLOAD_PATH}/${cleanPath}`;
};

const getHeaders = (token?: string) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const authToken = getAuthToken(token);
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    return headers;
};

const api = {
    // AUTH
    register: async (data: object) => {
        const res = await fetch(`${API_BASE}/auth/register`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(data) });
        return res.json();
    },
    sendSignupOTP: async (email: string) => {
        const res = await fetch(`${API_BASE}/auth/send-signup-otp`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }) });
        return res.json();
    },
    login: async (email: string, password: string) => {
        const res = await fetch(`${API_BASE}/auth/login`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email, password }) });
        return res.json();
    },
    forgotPassword: async (email: string) => {
        const res = await fetch(`${API_BASE}/auth/forgot-password`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }) });
        return res.json();
    },
    resetPassword: async (token: string, password: string) => {
        const res = await fetch(`${API_BASE}/auth/reset-password/${token}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify({ password }) });
        return res.json();
    },
     adminLoginAsUser: (token: string, userId: string) =>
    fetch(`${API_BASE}/admin/login-as/${userId}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    }).then(res => res.json()),
    // USER
    getMe: async (token: string) => {
        const res = await fetch(`${API_BASE}/users/me`, { headers: getHeaders(token) });
        return res.json();
    },
    updateMe: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/users/me`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },
    changePassword: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/users/change-password`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },

    // PROPERTIES (own)
    getStats: async (token: string) => {
        const res = await fetch(`${API_BASE}/properties/stats`, { headers: getHeaders(token) });
        return res.json();
    },
    getMyProperties: async (token: string) => {
        const res = await fetch(`${API_BASE}/properties/my`, { headers: getHeaders(token) });
        return res.json();
    },
    createProperty: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/properties`, { method: 'POST', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },
    deleteProperty: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/properties/${id}`, { method: 'DELETE', headers: getHeaders(token) });
        return res.json();
    },

    // ADMIN - System-wide
    adminGetStats: async (token: string) => {
        const res = await fetch(`${API_BASE}/admin/stats`, { headers: getHeaders(token) });
        return res.json();
    },
    adminGetUsers: async (token: string) => {
        const res = await fetch(`${API_BASE}/admin/users`, { headers: getHeaders(token) });
        return res.json();
    },
    adminDeleteUser: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/admin/users/${id}`, { method: 'DELETE', headers: getHeaders(token) });
        return res.json();
    },
    adminDeleteMultipleUsers: async (token: string, userIds: string[]) => {
        const res = await fetch(`${API_BASE}/admin/users/bulk`, { 
            method: 'DELETE', 
            headers: getHeaders(token),
            body: JSON.stringify({ userIds })
        });
        return res.json();
    },
    adminChangeRole: async (token: string, id: string, role: string) => {
        const res = await fetch(`${API_BASE}/admin/users/${id}/role`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify({ role }) });
        return res.json();
    },
    adminUpdateUser: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/admin/users/${id}`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },
    adminGetAllProperties: async (token: string) => {
        const res = await fetch(`${API_BASE}/admin/properties`, { headers: getHeaders(token) });
        return res.json();
    },
    adminDeleteProperty: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/admin/properties/${id}`, { method: 'DELETE', headers: getHeaders(token) });
        return res.json();
    },
    adminToggleFeatured: async (token: string, id: string, isFeatured: boolean) => {
        const res = await fetch(`${API_BASE}/admin/properties/${id}/featured`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify({ isFeatured }) });
        return res.json();
    },
    adminApproveProperty: async (token: string, id: string, isApproved: boolean) => {
        const res = await fetch(`${API_BASE}/admin/properties/${id}/approve`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify({ isApproved }) });
        return res.json();
    },
    adminUpdateProperty: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/admin/properties/${id}`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },
    adminGetBookings: async (token: string) => {
        const res = await fetch(`${API_BASE}/bookings`, { headers: getHeaders(token) });
        return res.json();
    },
    adminUpdateBooking: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/bookings/${id}`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },
    adminDeleteBooking: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/bookings/${id}`, { method: 'DELETE', headers: getHeaders(token) });
        return res.json();
    },

    // PROJECTS
    adminGetAllProjects: async (token: string) => {
        const res = await fetch(`${API_BASE}/projects/admin`, { headers: getHeaders(token) });
        return res.json();
    },
    adminCreateProject: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/projects`, { method: 'POST', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },
    adminUpdateProject: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/projects/${id}`, { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(data) });
        return res.json();
    },
    adminDeleteProject: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/projects/${id}`, { method: 'DELETE', headers: getHeaders(token) });
        return res.json();
    },
    adminBulkDeleteProjects: async (token: string, ids: string[]) => {
        const res = await fetch(`${API_BASE}/projects/bulk-delete`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ ids })
        });
        return res.json();
    },
    getAllProjects: async () => {
        const res = await fetch(`${API_BASE}/projects`);
        return res.json();
    },
    getMyProjects: async (token: string) => {
        const res = await fetch(`${API_BASE}/projects/my`, { headers: getHeaders(token) });
        return res.json();
    },
    deleteProject: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/projects/${id}`, { method: 'DELETE', headers: getHeaders(token) });
        return res.json();
    },

    getUserProfile: async (token: string) => {
        const res = await fetch(`${API_BASE}/users/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        return res.json();
    },

    assignPackage: async (token: string, packageId: string) => {
        const res = await fetch(`${API_BASE}/users/assign-package`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ packageId })
        });
        return res.json();
    },

    adminAssignPackage: async (token: string, userId: string, packageId: string, startDate?: string, endDate?: string) => {
        const res = await fetch(`${API_BASE}/admin/users/${userId}/assign-package`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ packageId, startDate, endDate })
        });
        return res.json();
    },

    adminUnassignPackage: async (token: string, userId: string) => {
        const res = await fetch(`${API_BASE}/admin/users/${userId}/unassign-package`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return res.json();
    },

    sendPackageInquiry: async (token: string, packageId: string) => {
        const res = await fetch(`${API_BASE}/leads/package-inquiry`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ packageId })
        });
        return res.json();
    },
    getProjectById: async (id: string) => {
        const res = await fetch(`${API_BASE}/projects/${id}`);
        return res.json();
    },

    // Upload
    uploadImages: async (token: string, files: File[]) => {
        const formData = new FormData();
        files.forEach(file => formData.append('images', file));
        const res = await fetch(`${API_BASE}/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }, // Do not set Content-Type, FormData sets it automatically with boundary
            body: formData
        });
        return res.json();
    },
  
    // PACKAGES
    getPackages: async (type?: string) => {
        const url = type ? `${API_BASE}/packages?type=${type}` : `${API_BASE}/packages`;
        const res = await fetch(url);
        return res.json();
    },
    adminGetPackageInquiries: async (token: string) => {
        const res = await fetch(`${API_BASE}/leads?source=Package Inquiry`, { headers: getHeaders(token) });
        return res.json();
    },
    adminDeletePackageInquiry: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/leads/${id}`, {
            method: 'DELETE',
            headers: getHeaders(token)
        });
        return res.json();
    },
    adminBulkDeletePackageInquiries: async (token: string, ids: string[]) => {
        const res = await fetch(`${API_BASE}/leads/bulk-delete`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ ids })
        });
        return res.json();
    },
    adminGetPackages: async (token: string, type?: string) => {
        const url = type ? `${API_BASE}/packages/admin?type=${type}` : `${API_BASE}/packages/admin`;
        const res = await fetch(url, { headers: getHeaders(token) });
        return res.json();
    },
    adminCreatePackage: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/packages`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminUpdatePackage: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/packages/${id}`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminDeletePackage: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/packages/${id}`, {
            method: 'DELETE',
            headers: getHeaders(token)
        });
        return res.json();
    },
    getLeadClassifiedPackages: async (token?: string) => {
        const res = await fetch(`${API_BASE}/lead-classified-packages`, { headers: getHeaders(token) });
        return res.json();
    },
    adminGetLeadClassifiedPackages: async (token: string) => {
        const res = await fetch(`${API_BASE}/lead-classified-packages/admin`, { headers: getHeaders(token) });
        return res.json();
    },
    adminCreateLeadClassifiedPackage: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/lead-classified-packages`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminUpdateLeadClassifiedPackage: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/lead-classified-packages/${id}`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminDeleteLeadClassifiedPackage: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/lead-classified-packages/${id}`, {
            method: 'DELETE',
            headers: getHeaders(token)
        });
        return res.json();
    },
    getSubscriptions: async (token: string) => {
        const res = await fetch(`${API_BASE}/classified-lead-subscriptions`, { headers: getHeaders(token) });
        return res.json();
    },
    purchasePlan: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/classified-lead-subscriptions`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminGetSubscriptions: async (token: string) => {
        const res = await fetch(`${API_BASE}/classified-lead-subscriptions/admin`, { headers: getHeaders(token) });
        return res.json();
    },
    adminUpdateSubscriptionStatus: async (token: string, id: string, status: string) => {
        const res = await fetch(`${API_BASE}/classified-lead-subscriptions/admin/${id}`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify({ status })
        });
        return res.json();
    },
    adminDeleteSubscription: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/classified-lead-subscriptions/admin/${id}`, {
            method: 'DELETE',
            headers: getHeaders(token)
        });
        return res.json();
    },
    adminBulkDeleteSubscriptions: async (token: string, ids: string[]) => {
        const res = await fetch(`${API_BASE}/classified-lead-subscriptions/admin/bulk-delete`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ ids })
        });
        return res.json();
    },
    getBuyerStats: async (token: string) => {
        const res = await fetch(`${API_BASE}/users/buyer-stats`, { headers: getHeaders(token) });
        return res.json();
    },
    toggleWishlist: async (token: string, itemId: string, itemType: 'property' | 'project' = 'property') => {
        const res = await fetch(`${API_BASE}/users/wishlist/toggle`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ itemId, itemType })
        });
        return res.json();
    },
    getWishlist: async (token: string) => {
        const res = await fetch(`${API_BASE}/users/wishlist`, { headers: getHeaders(token) });
        return res.json();
    },

    // SYSTEM SETTINGS
    getSystemSettings: async () => {
        const res = await fetch(`${API_BASE}/system-settings`, { headers: getHeaders() });
        return res.json();
    },
    updateSystemSettings: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/system-settings`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    generateSitemap: async (token: string) => {
        const res = await fetch(`${API_BASE}/system-settings/generate-sitemap`, {
            method: 'POST',
            headers: getHeaders(token)
        });
        return res.json();
    },
    bulkSyncInventory: async (token: string, projectId: string, units: any[]) => {
        const res = await fetch(`${API_BASE}/inventory/bulk-sync`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ projectId, units })
        });
        return res.json();
    },

    // LANDMARK CATEGORIES
    getLandmarkCategories: async () => {
        const res = await fetch(`${API_BASE}/landmark-categories`, { headers: getHeaders() });
        return res.json();
    },
    adminCreateLandmarkCategory: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/landmark-categories`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminUpdateLandmarkCategory: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/landmark-categories/${id}`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminDeleteLandmarkCategory: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/landmark-categories/${id}`, {
            method: 'DELETE',
            headers: getHeaders(token)
        });
        return res.json();
    },
    adminGetSubAdmins: async (token: string) => {
        const res = await fetch(`${API_BASE}/admin/sub-admins`, { headers: getHeaders(token) });
        return res.json();
    },
    adminCreateSubAdmin: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/admin/sub-admins`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminUpdateSubAdmin: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/admin/sub-admins/${id}`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    
    // ICON LIBRARY
    getIcons: async () => {
        const res = await fetch(`${API_BASE}/icons`, { headers: getHeaders() });
        return res.json();
    },
    adminCreateIcon: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/icons`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminUpdateIcon: async (token: string, id: string, data: object) => {
        const res = await fetch(`${API_BASE}/icons/${id}`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },
    adminDeleteIcon: async (token: string, id: string) => {
        const res = await fetch(`${API_BASE}/icons/${id}`, {
            method: 'DELETE',
            headers: getHeaders(token)
        });
        return res.json();
    },
    getScoreSettings: async () => {
        const res = await fetch(`${API_BASE}/score-settings`);
        return res.json();
    },
    updateScoreSettings: async (token: string, data: object) => {
        const res = await fetch(`${API_BASE}/score-settings`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data)
        });
        return res.json();
    },

    // MEDIA FOLDERS
    getMediaFolders: async (token?: string) => {
        const res = await fetch(`${API_BASE}/media-folders`, {
            headers: getHeaders(token)
        });
        return safeJsonResponse(res);
    },
    getMediaFolder: async (folderNumber: number | string, token?: string) => {
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}`, {
            headers: getHeaders(token)
        });
        return safeJsonResponse(res);
    },
    createMediaFolder: async (token?: string, data?: object) => {
        const res = await fetch(`${API_BASE}/media-folders`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data || {})
        });
        return safeJsonResponse(res);
    },
    updateMediaFolder: async (token?: string, folderNumber?: number | string, data?: object) => {
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data || {})
        });
        return safeJsonResponse(res);
    },
    deleteMediaFolder: async (token?: string, folderNumber?: number | string) => {
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}`, {
            method: 'DELETE',
            headers: getHeaders(token)
        });
        return safeJsonResponse(res);
    },
    uploadMediaFolderFiles: async (token?: string, folderNumber?: number | string, subFolder?: string, files?: File[], extraData?: Record<string, string>) => {
        const formData = new FormData();
        (files || []).forEach(f => formData.append('files', f));
        if (extraData) {
            Object.entries(extraData).forEach(([k, v]) => formData.append(k, v));
        }
        const authToken = getAuthToken(token);
        const headers: Record<string, string> = {};
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}/upload?subFolder=${encodeURIComponent(subFolder || 'gallery/other')}`, {
            method: 'POST',
            headers,
            body: formData
        });
        return safeJsonResponse(res);
    },
    deleteMediaFolderFile: async (token?: string, folderNumber?: number | string, data?: { subFolder?: string, filePath?: string, fileId?: string }) => {
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}/delete-file`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify(data || {})
        });
        return safeJsonResponse(res);
    },
    updateMediaFolderItem: async (token?: string, folderNumber?: number | string, data?: object) => {
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}/item`, {
            method: 'PUT',
            headers: getHeaders(token),
            body: JSON.stringify(data || {})
        });
        return safeJsonResponse(res);
    },
    bulkDeleteMediaFolderFiles: async (token?: string, folderNumber?: number | string, files?: Array<{ subFolder?: string, filePath?: string, fileId?: string }>) => {
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}/bulk-delete`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ files: files || [] })
        });
        return safeJsonResponse(res);
    },
    bulkDeleteMediaFolders: async (token?: string, folderNumbers?: number[]) => {
        const res = await fetch(`${API_BASE}/media-folders/bulk-delete-folders`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ folderNumbers: folderNumbers || [] })
        });
        return safeJsonResponse(res);
    },
    syncMediaFolder: async (token?: string, folderNumber?: number | string) => {
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}/sync`, {
            method: 'POST',
            headers: getHeaders(token)
        });
        return safeJsonResponse(res);
    },

    // ZIP & BACKUP HELPERS
    downloadSampleZip: async () => {
        try {
            // First attempt: same-origin static file from client/public
            const staticRes = await fetch('/sample-project-media-folder.zip');
            if (staticRes.ok) {
                const blob = await staticRes.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'sample-project-media-folder.zip';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                return;
            }
        } catch (e) {
            console.warn('Direct static sample zip fetch failed, falling back to API:', e);
        }

        // Fallback: API endpoint
        const res = await fetch(`${API_BASE}/media-folders/sample-zip`);
        if (!res.ok) {
            const err = await safeJsonResponse(res);
            throw new Error(err.message || 'Failed to download sample ZIP');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sample-project-media-folder.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    },
    downloadFolderBackupZip: async (folderNumber: number | string, token?: string) => {
        const authToken = getAuthToken(token);
        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}/backup-zip`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        if (!res.ok) {
            const err = await safeJsonResponse(res);
            throw new Error(err.message || 'Failed to download backup ZIP');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Project-Media-Folder-#${folderNumber}-backup.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    },
    downloadAllFoldersBackupZip: async (token?: string) => {
        const authToken = getAuthToken(token);
        const res = await fetch(`${API_BASE}/media-folders/backup/all`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        if (!res.ok) {
            const err = await safeJsonResponse(res);
            throw new Error(err.message || 'Failed to download all folders backup');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `All-Project-Media-Folders-backup-${new Date().toISOString().slice(0, 10)}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    },
    uploadMediaFolderZip: async (token: string | undefined, folderNumber: number | string, zipFile: File) => {
        const formData = new FormData();
        formData.append('zipFile', zipFile);
        const authToken = getAuthToken(token);
        const headers: Record<string, string> = {};
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

        const res = await fetch(`${API_BASE}/media-folders/${folderNumber}/upload-zip`, {
            method: 'POST',
            headers,
            body: formData
        });
        return safeJsonResponse(res);
    },
    createMediaFolderFromZip: async (token: string | undefined, zipFile: File, data?: { folderNumber?: number | string, folderName?: string, description?: string }) => {
        const formData = new FormData();
        formData.append('zipFile', zipFile);
        if (data?.folderNumber) formData.append('folderNumber', String(data.folderNumber));
        if (data?.folderName) formData.append('folderName', data.folderName);
        if (data?.description) formData.append('description', data.description);

        const authToken = getAuthToken(token);
        const headers: Record<string, string> = {};
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

        const res = await fetch(`${API_BASE}/media-folders/create-from-zip`, {
            method: 'POST',
            headers,
            body: formData
        });
        return safeJsonResponse(res);
    },
    uploadBulkMediaFolderZip: async (token: string | undefined, zipFiles: File[]) => {
        const formData = new FormData();
        zipFiles.forEach(file => {
            formData.append('zipFiles', file);
        });
        const authToken = getAuthToken(token);
        const headers: Record<string, string> = {};
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

        const res = await fetch(`${API_BASE}/media-folders/bulk-zip`, {
            method: 'POST',
            headers,
            body: formData
        });
        return safeJsonResponse(res);
    },

    // MONGODB BACKUP & MIGRATION HELPERS
    downloadMongoBackupZip: async (token?: string) => {
        const authToken = getAuthToken(token);
        const res = await fetch(`${API_BASE}/admin/backup-mongodb`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        if (!res.ok) {
            const err = await safeJsonResponse(res);
            throw new Error(err.message || 'Failed to download MongoDB backup');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mongodb-backup-myproperty-guru-${new Date().toISOString().slice(0, 10)}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    },
    cloneToLocalMongo: async (token?: string, localUri?: string) => {
        const res = await fetch(`${API_BASE}/admin/clone-to-local-mongodb`, {
            method: 'POST',
            headers: getHeaders(token),
            body: JSON.stringify({ localUri: localUri || 'mongodb://127.0.0.1:27017/myproperty-guru' })
        });
        return safeJsonResponse(res);
    }
};

export default api;


